/**
 * @module XVP
 */
import config from 'config';
import { dispatches } from 'lib/event-dispatcher';
import Logger from 'lib/logger';
import { XVP_API_CONFIGS, XVP_API_URLS } from 'constants/xvp-services';
import SplunkLogger from 'lib/telemetry/splunk-logger';
import { lookupError } from 'lib/helpers';
import Session from '../session';

window.onbeforeunload = (e) => {
  window.isWindowUnloaded = true;
};

/**
 * Singleton module for working with XVP endpoints. This object should be fed a
 * valid session (xsct, sat, deviceId, xboAccountId) before interaction with XVP.
 */

const CLIENT_ID = 'stream-chromecast';
const logger = new Logger('XVP', { background: 'blue', color: 'white' });
@dispatches('xvp')

class XVP {
  _session = {
    partnerId: '',
    deviceId: '',
    clientId: '',
    accountId: '',
    xboAccountId: '',
    xsct: '',
    notBefore: 0,
    notOnOrAfter: 0
  };
  params = {
    accountId: '',
    deviceId: '',
    partnerId: ''
  };
  headers = {
    'Accept': 'application/json',
    'Authorization': '',
    'Content-Type': 'application/json'
  };
  rightsRequestBody = {}


  /**
  * Note: partnerId, clientId are also sent in the _session
  */
  constructor() {}

  get session() {
    return this._session;
  }

  set session(tokenSummary) {
    const token = Object.assign({}, tokenSummary, tokenSummary.tokenSummary) || tokenSummary || {};
    if (!token.xsct || !token.serviceAccessToken || !token.deviceId || !token.xboAccountId) {
      logger.log('set session failed, missing necessary session data');
      return;
    }
    this._session = token;
    this.rightsRequestBody = { xsct: token.xsct };
    this._setApiParams(token);

    return this._session;
  }

  getFeatures() {
    return this._session && this._session.featuresResult && this._session.featuresResult.features || {};
  }

  getFeature(xvpFeature) {
    return Boolean(this.getFeatures()[xvpFeature] === 'true') || false;
  }

  _setApiParams(token) {
    this.params = {
      accountId: token.xboAccountId,
      deviceId: token.deviceId,
      partnerId: token.partnerId
    };

    this.headers = {
      ...this.headers,
      Authorization: token.serviceAccessToken
    };
  }


  /**
   * Send XVP request using endpoint defined in supportedResources
   * @param {object} args - request params
   * @return {Promise}
   */
  async send(args) {
    const endPoint = args.endPoint;
    const xvpApiConfig = XVP_API_CONFIGS[endPoint];
    window.isWindowUnloaded = false;

    if (!xvpApiConfig) {
      logger.error(`Error: Could not find XVP_API_CONFIGS for endPoint: ${endPoint}`);
      return;
    }

    if (this._areTokensExpired()) {
      this.session = await Session.refreshSession();
    }

    args = {
      ...args,
      pathParams: {
        ...args.pathParams,
        ...this.params
      }
    };

    const { pathParams, queryParams, body = null } = args;
    const request = this._createRequest(endPoint, pathParams, queryParams);
    request.options.headers = this.headers;
    request.options.pathParams = pathParams;

    if (body) {
      request.options.body = JSON.stringify(body);
    }

    if (xvpApiConfig.serviceName === 'RIGHTS') {
      request.options.body = JSON.stringify(this.rightsRequestBody);
    }

    const sendDate = (new Date()).getTime();
    let responseTimeMs = 0;

    try {
      const response = await fetch(request.url, request.options);

      const receiveDate = (new Date()).getTime();
      responseTimeMs = receiveDate - sendDate;
      let data = {};

      if (response.status !== 204) {
        data = await response.json();
      }

      const splunkObj = {
        headers: {
          traceId: response.headers.get('trace-id'),
          traceResponse: response.headers.get('traceresponse')
        },
        xhr: {
          ok: response.ok,
          status: response.status,
          subCode: data.statusSubCode || '',
          statusText: response.statusText,
          type: response.type,
          url: response.url
        },
        request: {
          params: args
        },
        responseTime: responseTimeMs
      };

      if (response.status >= 400 || !response.ok) {
        throw ({
          errorMessage: data.title,
          errorReason: data.failureMessage,
          ...splunkObj
        });
      }

      SplunkLogger.onSuccess(splunkObj);

      return data;
    } catch (error) {
      /**
       * Even a success call can come into the catch()
       * if the window is unloaded before the response comes back to the browser.
      */
      if (window.isWindowUnloaded) {
        return;
      }

      /**
       * Browser errors can be strings such as:
       * 'TypeError: Failed to fetch'
       * 'TypeError: Browser refused to fetch'
       * etc
      */
      let clientBrowserError = {};
      if (!error.errorMessage) {
        clientBrowserError = {
          errorMessage: `${error}`
        };
      }

      const xhr = error.xhr || {};
      const errorDetails = lookupError(['api', endPoint, xhr.status, xhr.subCode]);

      const errorObj = {
        ...args,
        ...error,
        ...clientBrowserError,
        lookup: errorDetails,
        url: request.url
      };

      SplunkLogger.onError(errorObj);

      throw (errorObj);
    }
  }

  _createRequest(endPoint, pathParams, queryParams) {
    const apiProps = XVP_API_CONFIGS[endPoint];
    let path = apiProps['path'];
    const serviceName = apiProps['serviceName'];
    const apiEnvironment = config.environment.API_ENV || 'prod';
    const domainName = XVP_API_URLS[serviceName][apiEnvironment];

    Object.keys(pathParams).forEach((key) => {
      path = path.replace(`{${key}}`, pathParams[key]);
    });

    path += `?clientId=${CLIENT_ID}`;
    queryParams &&
      Object.keys(queryParams).forEach((key) => {
        path += `&${key}=${queryParams[key]}`;
      });

    return {
      url: domainName + path,
      options: {
        method: apiProps['method'] || 'GET'
      }
    };
  }

  _areTokensExpired() {
    // if notOnOrAfter value isn't present, use the notBefore value + 24 hours
    const expiry = this.session.notOnOrAfter || (this.session.notBefore && this.session.notBefore + Number(8.64e7)) || 0;

    if (!expiry || expiry <= 0) {
      return false;
    }

    const expiryMinusTwoHours = expiry - 72e5;

    return (Date.now() > expiryMinusTwoHours);
  }
}

export default new XVP();
export { XVP };
